// Data model (docs/04-data-model.md). Coordinates are A pixel coordinates (D-33); time is in ticks.

export type SourceId = 'A' | 'B1' | 'B2' | 'B3';
export const SOURCE_IDS: readonly SourceId[] = ['A', 'B1', 'B2', 'B3'];
export const B_IDS: readonly SourceId[] = ['B1', 'B2', 'B3'];
/** Names shown to the user: A, B, C, D (the ids B1–B3 stay in project files and code). */
export const SLOT_LABELS: Record<SourceId, string> = { A: 'A', B1: 'B', B2: 'C', B3: 'D' };
export function slotLabel(id: SourceId): string {
  return SLOT_LABELS[id] ?? id;
}

export type EditType = 'ET-1' | 'ET-2' | 'ET-3' | 'ET-4' | 'ET-5';

export interface Point {
  x: number;
  y: number;
}
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type FrameMapping = { mode: 'one-to-one' } | { mode: 'ratio'; ratio: 2 | 3 | 4 } | { mode: 'time' };

export type AlignMode = 'center' | 'stretch' | 'fit' | 'topleft' | 'manual';

/** B pixel (bx, by) maps to A position (dx + bx*sx, dy + by*sy) (F-03). */
export interface SpatialTransform {
  mode: AlignMode;
  dx: number;
  dy: number;
  sx: number;
  sy: number;
  appliedFromSuggestion: boolean;
}

export interface VideoSpec {
  container: string;
  codec: string;
  width: number;
  height: number;
  pixelAspectRatio: number;
  rotation: 0 | 90 | 180 | 270;
  frameRate: number;
  frameCount: number;
  durationSec: number;
  /** 16 for 16-bit PNG and EXR sequences (F-01.12). */
  bitDepth: 8 | 10 | 12 | 16;
  chromaSubsampling: '4:2:0' | '4:2:2' | '4:4:4';
  colorPrimaries?: string;
  transferCharacteristics?: string;
  matrixCoefficients?: string;
  range?: 'limited' | 'full';
  bitrateKbps?: number;
  maxGopLength?: number;
  startTimecode?: { text: string; dropFrame: boolean };
  isVariableFrameRate: boolean;
  isInterlaced: boolean;
  audio?: { codec: string; sampleRate: number; channels: number; durationSec: number };
  /** Whether the browser reported the decoder config as supported. */
  decodable?: boolean;
}

export interface SourceFileInfo {
  name: string;
  size: number;
  lastModified: number;
  fingerprint: string;
}

export interface ColorOverride {
  matrixCoefficients?: 'bt709' | 'bt601';
  range?: 'limited' | 'full';
}

/** An image sequence as a source (F-01.12): file identity is the whole set, fps is chosen by the user. */
export interface SequenceInfo {
  kind: 'png' | 'exr';
  /** Name pattern, e.g. "shot010_####.exr" (also Source.file.name). */
  pattern: string;
  /** Frame number of the first file; frame index i is file number first + i. */
  first: number;
  count: number;
  /** How EXR data is shown (gamut, transfer, exposure). */
  exrView?: import('./sequence').ExrView;
}

export interface Source {
  id: SourceId;
  role: 'original' | 'ai';
  displayName: string;
  /** For a sequence: pattern name, total size, newest date and a fingerprint over the set. */
  file: SourceFileInfo;
  sequence?: SequenceInfo;
  /** Downloaded from a pasted link (F-01.13): downloaded again when the file cannot be opened. */
  remote?: import('./remote-media').RemoteRef;
  spec: VideoSpec;
  colorOverride?: ColorOverride;
  frameMapping: FrameMapping;
  /** Whether frameMapping was chosen by the user rather than detected (F-01.8). */
  frameMappingManual?: boolean;
  frameOffset: number;
  spatialTransform: SpatialTransform;
}

export type LoopMode = 'loop' | 'once' | 'ping-pong';

export interface PlaybackState {
  tick: number;
  inPoint?: number;
  outPoint?: number;
  loopMode: LoopMode;
  speed: number;
}

export type Layout = 'side-by-side' | 'side-by-side-zoom' | 'quad' | 'focus' | 'single';
export type CompareMode = 'side-by-side' | 'wipe' | 'overlay' | 'flicker' | 'difference';
export type ZoomPercent = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 1000 | 1200 | 1400 | 1600 | 1800 | 2000 | 2500 | 3000;
/** Zoom-pane levels (D-22): 100 % steps up to 1000 %, 200 % steps up to 2000 %, then 2500 % and 3000 %. */
export const ZOOM_LEVELS: readonly ZoomPercent[] = [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1200, 1400, 1600, 1800, 2000, 2500, 3000];
export type Channel = 'rgb' | 'r' | 'g' | 'b' | 'luma';
/** Difference of everything, of the light-and-colour layer only (F-05.6) or of the detail layer only (F-05.7). */
export type DiffBand = 'all' | 'low' | 'high';

export interface LayoutState {
  layout: Layout;
  focusSourceId?: SourceId;
  /** The B shown on the right in one-to-one layouts. */
  selectedB: SourceId;
  /** Reference of wipe / overlay / flicker / difference: A when absent, or another version (F-05.9). */
  compareBase?: SourceId;
  compareMode: CompareMode;
  sideBySideDirection: 'horizontal' | 'vertical';
  wipe: { position: number; angleDeg: number };
  overlayOpacity: number;
  difference: { gain: number; display: 'gray' | 'heat'; lumaOnly: boolean; maskOnly: boolean; band?: DiffBand };
  view: { zoomPercent: number | 'fit'; center: Point; sampling: 'nearest' | 'smooth' };
  crosshair: { position: Point; zoomPercent: ZoomPercent; visible: boolean };
  zoomPaneRatio: number;
  /** Zoom panes show the difference instead of A|B (F-06.6). */
  zoomDifference: boolean;
  /** Semi-transparent 10 A-pixel grid in the zoom panes (F-06.10). */
  zoomGrid: boolean;
  channel: Channel;
  textureView: boolean;
  maskOverlay: boolean;
  maskFocus: boolean;
}

export interface Levels {
  inBlack: number;
  inWhite: number;
  outBlack: number;
  outWhite: number;
  mid: number;
}

export interface ViewAdjustmentValues {
  exposureStops: number;
  gamma: number;
  levels: Levels;
  perChannel?: { r: Levels; g: Levels; b: Levels };
}

export interface ViewAdjustment extends ViewAdjustmentValues {
  enabled: boolean;
  linked: boolean;
  perSource?: Partial<Record<SourceId, ViewAdjustmentValues & { enabled: boolean }>>;
  presetId?: string;
}

export interface MaskSourceBase {
  id: string;
  label: string;
  computedOn: SourceId;
}

export type MaskTarget = 'person' | 'face' | 'faceHair';

export type MaskSourceParams = MaskSourceBase & {
  kind: 'person-auto';
  model: string;
  /** Part of the person the mask covers (F-08.4): the whole person (default), the face skin, or face and hair. */
  target?: MaskTarget;
  /** Engine: MediaPipe in the browser (default) or SAM 3 on the local helper (F-08.9, D-50). */
  engine?: 'mediapipe' | 'sam3';
  /** SAM 3: what to mask, in English ("person", "face", "left hand"…). */
  samPrompt?: string;
  threshold: number;
  featherPx: number;
  /** Positive expands, negative contracts (F-08.12). */
  expandPx: number;
};

export interface Region {
  id: string;
  kind: 'intended' | 'protected' | 'other';
  label: string;
  maskSourceId: string;
  enabled: boolean;
  overlayColor?: string;
}

export interface VersionRating {
  sourceId: SourceId;
  score?: number;
  note: string;
}

export interface Project {
  schemaVersion: string;
  appVersion: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  editTypes: EditType[];
  sources: Source[];
  compareScale: 1 | 2 | 3 | 4;
  playback: PlaybackState;
  layout: LayoutState;
  viewAdjustment: ViewAdjustment;
  audioSourceId: SourceId | null;
  audioVolume: number;
  timecodeMode: 'embedded' | 'from-zero';
  /** When interpolated B exist: step only over ticks with an original A frame (F-02.9). */
  originalFramesOnly: boolean;
  /** Ticks per A frame that the stored tick values (playhead, in/out) are expressed in. */
  tickScale: number;
  maskSources: MaskSourceParams[];
  regions: Region[];
  ratings: VersionRating[];
  /** Automatic quality checks of this shot's versions (F-10.7), kept with the project. */
  qualityChecks: Partial<Record<SourceId, import('./quality').StoredQuality>>;
  /** What the AI edit of this shot was meant to do, told to the AI look so it is not reported (F-10.10). */
  aiBrief: string;
  /** Fields from newer/unknown versions are preserved on save (docs/04-data-model.md). */
  extra?: Record<string, unknown>;
}

export interface UserSettings {
  language: 'zh-TW' | 'en';
  degradeMode: 'smooth' | 'quality';
  frameNumberBase: 0 | 1;
  flickerHz: number;
  zoomCenterMark: boolean;
  alignAutoDifference: boolean;
  shortcuts?: Record<string, string>;
  /** Project tree collapsed to a narrow rail (F-18). */
  sidebarCollapsed?: boolean;
  /** Height of the project tree in px (the divider above the tool panel was dragged); unset = automatic. */
  sideTreeHeight?: number;
  /** Videos from links / moonland (F-01.13): saved into a chosen folder (default) or kept in memory only. */
  linkSave?: 'folder' | 'none';
}
